import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/auth-context";
import { useAuthGuard } from "@/hooks/use-auth-guard";
import { getIsRedirecting, isInGracePeriod } from "@/lib/api";

interface RequireAuthProps {
  children?: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const { token, selectedSubscriptionId, selectedCompanyId } = useAuth();
  const location = useLocation();
  const { isValidating, isValid, isReady, shouldRedirect, redirectTo } = useAuthGuard();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">Validando autenticación...</p>
        </div>
      </div>
    );
  }

  // Durante revalidaciones periódicas (refreshInterval), si ya tenemos sesión válida,
  // no debemos desmontar el árbol de rutas (esto resetea formularios con estado local).
  // En ese caso, dejamos renderizar la app y la validación ocurre "en background".
  if (isValidating && isValid) {
    return children ? <>{children}</> : <Outlet />;
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="text-sm text-muted-foreground">Validando autenticación...</p>
        </div>
      </div>
    );
  }

  if (shouldRedirect) {
    // No redirigir si estamos en período de gracia (después de seleccionar sociedad)
    if (isInGracePeriod()) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-muted-foreground">Validando autenticación...</p>
          </div>
        </div>
      );
    }

    if (getIsRedirecting()) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-sm text-muted-foreground">Redirigiendo...</p>
          </div>
        </div>
      );
    }

    return <Navigate to={redirectTo || "/login"} state={{ from: location }} replace />;
  }

  if (isValid && !selectedSubscriptionId) {
    return <Navigate to="/select-subscription" state={{ from: location }} replace />;
  }

  const needsCompany = selectedSubscriptionId !== "SYSTEM" && !selectedCompanyId;
  if (isValid && selectedSubscriptionId && needsCompany) {
    return <Navigate to="/select-company" state={{ from: location }} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};
